import React, { useEffect, useMemo, useState } from "react";
import {
  Line,
  LineChart,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { fetchFullDashboard } from "../services/googleAnalytics.service";

function formatShortDate(isoDate) {
  if (!isoDate) return "";
  // isoDate expected: YYYY-MM-DD
  const [yyyy, mm, dd] = String(isoDate).split("-");
  if (!yyyy || !mm || !dd) return isoDate;
  return `${dd}/${mm}`;
}

function normalizeSourceLabel(raw) {
  const v = String(raw || "").trim();
  const lower = v.toLowerCase();

  if (!v) return "Unknown";
  if (lower === "(direct)" || lower === "direct") return "Direct Traffic";
  if (lower === "(not set)" || lower === "(not provided)" || lower === "not set")
    return "Unknown";
  if (lower === "unassigned") return "Other Traffic";

  // Common cleanup
  if (lower === "google") return "Google";
  if (lower === "facebook") return "Facebook";
  if (lower === "instagram") return "Instagram";
  if (lower === "linkedin") return "LinkedIn";
  if (lower === "bing") return "Bing";

  return v;
}

function classifySource(label) {
  const l = String(label || "").toLowerCase();
  if (l === "direct traffic") return "unknown";
  if (l === "unknown") return "unknown";
  if (l === "other traffic") return "unknown";
  return "known";
}

function normalizeChannelLabel(raw) {
  const v = String(raw || "").trim();
  const lower = v.toLowerCase();
  if (!v) return "Unknown";
  if (lower === "(not set)" || lower === "(not provided)" || lower === "not set")
    return "Unknown";
  if (lower === "unassigned") return "Other Traffic";
  return v;
}

function formatEventLabel(raw) {
  const v = String(raw || "").trim();
  if (!v) return "Unknown";
  return v
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function GoogleAnalyticsDashboard() {
  const [range, setRange] = useState("7d");
  const [overview, setOverview] = useState({
    users: 0,
    sessions: 0,
    events: 0,
    newUsers: 0,
  });
  const [trend, setTrend] = useState([]);
  const [sources, setSources] = useState([]);
  const [channels, setChannels] = useState([]);
  const [pages, setPages] = useState([]);
  const [topEvents, setTopEvents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetchFullDashboard(range);
        const payload = res?.data || {};

        if (!alive) return;

        const o = payload.overview || {};
        setOverview({
          users: Number(o.users) || 0,
          sessions: Number(o.sessions) || 0,
          events: Number(o.events) || 0,
          newUsers: Number(o.newUsers) || 0,
        });
        setTrend(Array.isArray(payload.trend) ? payload.trend : []);
        setSources(Array.isArray(payload.sources) ? payload.sources : []);
        setChannels(Array.isArray(payload.channels) ? payload.channels : []);
        setPages(Array.isArray(payload.pages) ? payload.pages : []);
        setTopEvents(Array.isArray(payload.topEvents) ? payload.topEvents : []);
        setDevices(Array.isArray(payload.devices) ? payload.devices : []);
        setLocations(Array.isArray(payload.locations) ? payload.locations : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load Google Analytics dashboard");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [range]);

  const chartData = useMemo(() => {
    return (Array.isArray(trend) ? trend : []).map((p) => ({
      date: p?.date,
      users: Number(p?.users) || 0,
      label: formatShortDate(p?.date),
    }));
  }, [trend]);

  const isEmpty =
    !loading &&
    !error &&
    chartData.length === 0 &&
    overview.users === 0 &&
    overview.sessions === 0 &&
    overview.events === 0 &&
    overview.newUsers === 0;

  const deviceColors = ["#2563EB", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
  const deviceChartData = useMemo(() => {
    return (Array.isArray(devices) ? devices : [])
      .map((d) => ({
        name: d?.device || "(not set)",
        value: Number(d?.users) || 0,
      }))
      .filter((d) => d.value > 0);
  }, [devices]);

  const renderTable = (columns, rows, getKey, emptyMessage = "No data available") => {
    if (!rows || rows.length === 0) {
      return (
        <div className="text-sm text-gray-600 py-6 text-center">
          {emptyMessage}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              {columns.map((c) => (
                <th key={c.key} className="py-2 px-3 font-medium">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={getKey(r, idx)} className="border-b last:border-b-0">
                {columns.map((c) => (
                  <td key={c.key} className="py-2 px-3 text-gray-900">
                    {c.render ? c.render(r) : r?.[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const topEventsChartData = useMemo(() => {
    return (Array.isArray(topEvents) ? topEvents : [])
      .map((e) => ({
        name: formatEventLabel(e?.event),
        count: Number(e?.count) || 0,
      }))
      .filter((e) => e.count > 0);
  }, [topEvents]);

  const sourcesDisplay = useMemo(() => {
    const list = (Array.isArray(sources) ? sources : []).map((s) => ({
      source: normalizeSourceLabel(s?.source),
      users: Number(s?.users) || 0,
    }));

    // Merge duplicates after renaming
    const merged = new Map();
    for (const item of list) {
      const key = item.source;
      merged.set(key, (merged.get(key) || 0) + item.users);
    }

    const mergedList = Array.from(merged.entries()).map(([source, users]) => ({
      source,
      users,
    }));

    mergedList.sort((a, b) => b.users - a.users);

    const known = mergedList.filter((x) => classifySource(x.source) === "known");
    const unknown = mergedList.filter((x) => classifySource(x.source) === "unknown");

    return { known, unknown };
  }, [sources]);

  const sourcesSummary = useMemo(() => {
    const getUsers = (label) =>
      sourcesDisplay.unknown
        .filter((x) => x.source === label)
        .reduce((sum, x) => sum + (Number(x.users) || 0), 0);

    const direct = getUsers("Direct Traffic");
    const unknown = getUsers("Unknown");
    const other = getUsers("Other Traffic");

    return { direct, unknown, other, total: direct + unknown + other };
  }, [sourcesDisplay]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Google Analytics</h1>
          <p className="text-sm text-gray-500">
            GA4 snapshot: active users, events, and daily trend
          </p>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600" htmlFor="ga-range">
            Range
          </label>
          <select
            id="ga-range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
            <div className="text-sm text-gray-700">Loading dashboard…</div>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl border border-red-200 p-6">
          <div className="text-sm font-medium text-red-700">Error</div>
          <div className="mt-1 text-sm text-red-600">{error}</div>
        </div>
      ) : isEmpty ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-600">
          No data available
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500">Users</div>
              <div className="mt-2 text-3xl font-semibold text-gray-900">
                {overview.users.toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500">Sessions</div>
              <div className="mt-2 text-3xl font-semibold text-gray-900">
                {overview.sessions.toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500">Events</div>
              <div className="mt-2 text-3xl font-semibold text-gray-900">
                {overview.events.toLocaleString()}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm text-gray-500">New Users</div>
              <div className="mt-2 text-3xl font-semibold text-gray-900">
                {overview.newUsers.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-900">Users trend</div>
                <div className="text-xs text-gray-500">
                  {range === "90d" ? "Last 90 days" : range === "30d" ? "Last 30 days" : "Last 7 days"}
                </div>
              </div>
            </div>

            <div className="mt-4 h-72">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-600">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#6B7280", fontSize: 12 }}
                      axisLine={{ stroke: "#E5E7EB" }}
                      tickLine={{ stroke: "#E5E7EB" }}
                    />
                    <YAxis
                      tick={{ fill: "#6B7280", fontSize: 12 }}
                      axisLine={{ stroke: "#E5E7EB" }}
                      tickLine={{ stroke: "#E5E7EB" }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 10, borderColor: "#E5E7EB" }}
                      labelFormatter={(_, items) => items?.[0]?.payload?.date || ""}
                      formatter={(value) => [value, "Users"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke="#2563EB"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm font-medium text-gray-900">Traffic sources</div>
              <div className="text-xs text-gray-500">
                Grouped to highlight meaningful traffic
              </div>

              <div className="mt-4 space-y-5">
                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">Known sources</div>
                  {renderTable(
                    [
                      { key: "source", label: "Source", render: (r) => r.source },
                      {
                        key: "users",
                        label: "Users",
                        render: (r) => Number(r.users || 0).toLocaleString(),
                      },
                    ],
                    sourcesDisplay.known,
                    (r, idx) => `${r?.source || "source"}-known-${idx}`,
                    "No campaign traffic detected yet"
                  )}
                </div>

                <div>
                  <div className="text-xs font-medium text-gray-700 mb-2">Unknown / other</div>
                  {renderTable(
                    [
                      { key: "source", label: "Source", render: (r) => r.source },
                      {
                        key: "users",
                        label: "Users",
                        render: (r) => Number(r.users || 0).toLocaleString(),
                      },
                    ],
                    sourcesDisplay.unknown,
                    (r, idx) => `${r?.source || "source"}-unknown-${idx}`
                  )}

                  {sourcesSummary.total > 0 ? (
                    <div className="mt-2 text-xs text-gray-700">
                      <span className="font-medium">Total traffic (unattributed):</span>{" "}
                      Direct: {sourcesSummary.direct.toLocaleString()}
                      {", "}Unknown: {sourcesSummary.unknown.toLocaleString()}
                      {", "}Other: {sourcesSummary.other.toLocaleString()}
                    </div>
                  ) : null}
                </div>
              </div>

          
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm font-medium text-gray-900">Channel breakdown</div>
              <div className="text-xs text-gray-500">Users by default channel group</div>
              <div className="mt-4">
                {renderTable(
                  [
                    { key: "channel", label: "Channel", render: (r) => normalizeChannelLabel(r.channel) },
                    { key: "users", label: "Users", render: (r) => Number(r.users || 0).toLocaleString() },
                  ],
                  channels,
                  (r, idx) => `${r?.channel || "channel"}-${idx}`
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5 lg:col-span-2">
              <div className="text-sm font-medium text-gray-900">Top pages</div>
              <div className="text-xs text-gray-500">Top 10 by views</div>
              <div className="mt-4">
                {renderTable(
                  [
                    { key: "page", label: "Page", render: (r) => r.page },
                    { key: "views", label: "Views", render: (r) => Number(r.views || 0).toLocaleString() },
                  ],
                  pages,
                  (r, idx) => `${r?.page || "page"}-${idx}`
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="text-sm font-medium text-gray-900">Devices</div>
              <div className="text-xs text-gray-500">Users by device category</div>
              <div className="mt-4 h-64">
                {deviceChartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-600">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip formatter={(v) => [Number(v).toLocaleString(), "Users"]} />
                      <Pie
                        data={deviceChartData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {deviceChartData.map((_, idx) => (
                          <Cell key={idx} fill={deviceColors[idx % deviceColors.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              {devices?.length ? (
                <div className="mt-3 space-y-1">
                  {devices.slice(0, 6).map((d, idx) => (
                    <div key={`${d?.device}-${idx}`} className="flex items-center justify-between text-xs">
                      <div className="text-gray-600">{d.device}</div>
                      <div className="text-gray-900 font-medium">
                        {Number(d.users || 0).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="text-sm font-medium text-gray-900">Events</div>
            <div className="text-xs text-gray-500">Top events (limited to top 10)</div>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="h-72">
                {topEventsChartData.length === 0 ? (
                  <div className="h-full flex items-center justify-center text-sm text-gray-600">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topEventsChartData} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fill: "#6B7280", fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fill: "#6B7280", fontSize: 12 }} allowDecimals={false} />
                      <Tooltip formatter={(v) => [Number(v).toLocaleString(), "Events"]} />
                      <Bar dataKey="count" fill="#2563EB" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div>
                {renderTable(
                  [
                    { key: "event", label: "Event", render: (r) => formatEventLabel(r.event) },
                    {
                      key: "count",
                      label: "Count",
                      render: (r) => Number(r.count || 0).toLocaleString(),
                    },
                  ],
                  (Array.isArray(topEvents) ? topEvents : []).filter((e) => Number(e?.count) > 0),
                  (r, idx) => `${r?.event || "event"}-${idx}`
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="text-sm font-medium text-gray-900">Locations</div>
            <div className="text-xs text-gray-500">Users by country</div>
            <div className="mt-4">
              {renderTable(
                [
                  { key: "country", label: "Country", render: (r) => r.country },
                  { key: "users", label: "Users", render: (r) => Number(r.users || 0).toLocaleString() },
                ],
                locations,
                (r, idx) => `${r?.country || "country"}-${idx}`
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

